import {
  Message,
  useGetGuestPdfLinkForConversationsMutation,
  useGetPdfLinkForConversationsMutation,
  useGenerateImageMutation,
  SETTING_TYPE,
  RootState,
} from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { Button, CircularProgress, Tabs, Tab, Card, CardBody, Tooltip } from '@nextui-org/react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SourceElementType } from '@circuitry-ai/doc-ui';
import 'viewerjs/dist/viewer.css';
import Viewer from 'viewerjs';
import { useSelector } from 'react-redux';
import {
  extractImagePathsFromMetadata,
  hasImagePresentInMetadata,
} from './messageMetadataUtils';

interface UnifiedMediaPreviewProps {
  msg: Partial<Message>;
  advisorId: string;
}

interface MediaUrlData {
  source_id: string;
  type: string;
  full_source_presigned_url: string;
  url: string;
}

interface ImageUrlData {
  presigned_url: string;
  s3_path: string;
}

interface MediaSource {
  source_id: string;
  file_name: string;
  page_labels: any[];
  element_type: string;
  element_refs?: Array<{ url: string }>;
  ranking?: number;
  mediaType: 'video' | 'audio' | 'youtube' | 'image';
  img_path?: string;
  image_description?: string;
}

const UnifiedMediaPreview = ({ msg, advisorId }: UnifiedMediaPreviewProps) => {
  const [
    getPdfLinkForConversations,
    { isLoading: generateMediaLoading, error: generateMediaError },
  ] = useGetPdfLinkForConversationsMutation();
  const [getGuestPdfLinkForConversations] = useGetGuestPdfLinkForConversationsMutation();

  const [
    generateImage,
    { isLoading: generateImageLoading, error: generateImageError },
  ] = useGenerateImageMutation();
  const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
  const { token: authToken } = useSelector((state: RootState) => state.auth);

  const isImageActive = advisorAbilities[SETTING_TYPE.IMAGES]?.is_active ?? false
  const isVideoActive = advisorAbilities[SETTING_TYPE.VIDEO]?.is_active ?? false
  const isAudioActive = advisorAbilities[SETTING_TYPE.AUDIO]?.is_active ?? false
  const isImagePresentInResponse = useMemo(
    () => hasImagePresentInMetadata(msg?.metaData),
    [msg?.metaData]
  );
  const [mediaUrlList, setMediaUrlList] = useState<MediaUrlData[]>([]);
  const [imageUrlList, setImageUrlList] = useState<ImageUrlData[]>([]);
  const [elementRefs, setElementRefs] = useState<string[]>([]);
  const [value, setValue] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [carouselIndices, setCarouselIndices] = useState<{ [key: string]: number }>({
    image: 0,
    video: 0,
    audio: 0,
    youtube: 0
  });
  const imgRef = useRef<any>(null);
  const viewerRef = useRef<any>(null);
  // Memoize the categorized sources to prevent unnecessary recalculations
  const categorizedSources = useMemo(() => {
    const allSources = msg?.metaData?.[1]?.source_ids || [];

    // Early return if no sources
    if (!allSources || allSources.length === 0) {
      return {
        image: [] as MediaSource[],
        video: [] as MediaSource[],
        audio: [] as MediaSource[],
        youtube: [] as MediaSource[]
      };
    }

    // First, get the top 5 source_ids based on ranking (similar to ImagePreview logic)
    // Create a copy of the array to avoid mutating the original (read-only) array
    const top5Sources = [...allSources]
      .sort((a: any, b: any) => {
        const rankA = a?.ranking ?? Infinity;
        const rankB = b?.ranking ?? Infinity;
        return rankA - rankB;
      })
      .slice(0, 5); // Take only top 5 overall

    // Now categorize only the top 5 sources into their respective media types
    const sources = {
      image: [] as MediaSource[],
      video: [] as MediaSource[],
      audio: [] as MediaSource[],
      youtube: [] as MediaSource[]
    };

    top5Sources.forEach((source: any) => {
      // Add null checks for source properties
      if (!source) return;

      const fileName = source?.file_name || '';
      const extension = fileName.split('.').pop()?.toLowerCase();

      let mediaType: 'video' | 'audio' | 'youtube' | 'image' | null = null;

      if (source?.element_type === SourceElementType.YOUTUBE) {
        mediaType = 'youtube';
      } else if (extension && SourceElementType.VIDEO && SourceElementType.VIDEO.includes(extension)) {
        mediaType = 'video';
      } else if (extension && SourceElementType.AUDIO && SourceElementType.AUDIO.includes(extension)) {
        mediaType = 'audio';
      } else if (isImagePresentInResponse && source?.img_path) {
        mediaType = 'image';
      } 
      if (mediaType) {
        sources[mediaType].push({
          ...source,
          mediaType,
          ranking: source.ranking ?? Infinity,
          // image_description: source?.image_description
        });
      }
    });

    return sources;
  }, [msg?.metaData, isImagePresentInResponse]);

  // Update state when categorized sources change
  useEffect(() => {
    // Set default tab to image first if available, then first available media type
    const availableTypes = Object.keys(categorizedSources).filter(
      type => {
        if (type === 'image') {
          return isImagePresentInResponse && imageUrlList.length > 0;
        }
        return categorizedSources[type as keyof typeof categorizedSources].length > 0;
      }
    );
    if (availableTypes.length > 0 && !selectedTab) {
      // Prioritize image tab if it exists
      if (isImagePresentInResponse && imageUrlList.length > 0) {
        setSelectedTab('image');
      } else {
        setSelectedTab(availableTypes[0]);
      }
    }
  }, [categorizedSources, selectedTab, imageUrlList.length, isImagePresentInResponse]);

  // Extract image paths for API call (specific to images)
  useEffect(() => {
    if (!isImagePresentInResponse) {
      setElementRefs([]);
      return;
    }
    setElementRefs(extractImagePathsFromMetadata(msg?.metaData));
  }, [msg?.metaData, isImagePresentInResponse]);

  // Fetch image URLs using generateImage API (only for images)
  useEffect(() => {
    if (elementRefs.length > 0) {
      (async () => {
        try {
          const ImageResponse = await generateImage({
            s3_paths: elementRefs,
          }).unwrap();
          setImageUrlList(ImageResponse?.data || []);
        } catch (e) {
          console.log((e as Error)?.message || 'Error Fetching Image');
        }
      })();
    } else {
      setImageUrlList([]);
    }
  }, [elementRefs, categorizedSources.image, generateImage]);

  // Setup image viewer (ViewerJS)
  useEffect(() => {

    let timerId: ReturnType<typeof setTimeout>;
    if (imgRef.current && imageUrlList.length > 0) {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      timerId = setTimeout(() => {
        try {
          if (!imgRef.current) {
            console.error('imgRef.current is null in timeout');
            return;
          }

          const images = imgRef.current.querySelectorAll('img');

          if (images.length === 0) {
            console.error('No images found in container');
            return;
          }

          viewerRef.current = new Viewer(imgRef.current, {
            title: false,
            focus: false,
            inline: false,
            button: true,
            navbar: true,
            tooltip: true,
            movable: true,
            zoomable: true,
            rotatable: true,
            scalable: true,
            transition: true,
            fullscreen: true,
            keyboard: true,
            toolbar: {
              zoomIn: { show: 1, size: 'large' },
              zoomOut: { show: 1, size: 'large' },
              oneToOne: { show: 1, size: 'large' },
              reset: { show: 1, size: 'large' },
              prev: { show: 1, size: 'large' },
              play: { show: 4, size: 'large' },
              next: { show: 1, size: 'large' },
              rotateLeft: { show: 1, size: 'large' },
              rotateRight: { show: 1, size: 'large' },
              flipHorizontal: { show: 1, size: 'large' },
              flipVertical: { show: 1, size: 'large' },
            },
          });
        } catch (error) {
          console.error('Error initializing ViewerJS:', error);
        }
      }, 500);
    } else {

    }
    return () => {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [imageUrlList]);

  // Fetch URLs for file-based media (video/audio) when sources are available
  useEffect(() => {
    const fileBasedSources = [...categorizedSources.video, ...categorizedSources.audio];

    if (fileBasedSources.length > 0 && advisorId) {
      (async () => {
        try {
          const payload = {
            advisor_id: advisorId,
            source_ids: fileBasedSources.map((source: MediaSource) => ({
              source_id: source.source_id,
              page_labels: []
            }))
          };

          const fetchMediaLinks = authToken
            ? getPdfLinkForConversations
            : getGuestPdfLinkForConversations;
          const mediaResponse = await fetchMediaLinks(payload).unwrap();
          setMediaUrlList(mediaResponse?.data || []);
        } catch (e) {
          console.log((e as Error)?.message || 'Error Fetching Media');
        }
      })();
    } else {
      setMediaUrlList([]);
    }
  }, [
    categorizedSources.video,
    categorizedSources.audio,
    advisorId,
    authToken,
    getPdfLinkForConversations,
    getGuestPdfLinkForConversations,
  ]);

  // Loading progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setValue((v) => (v >= 100 ? 0 : v + 10));
    }, 500);
    if (!generateMediaLoading) clearInterval(interval);
    return () => clearInterval(interval);
  }, [generateMediaLoading]);

  const getMediaUrlBySourceId = (sourceId: string): MediaUrlData | null => {
    if (!sourceId || !mediaUrlList || mediaUrlList.length === 0) {
      return null;
    }

    try {
      return mediaUrlList.find(item => item?.source_id === sourceId) || null;
    } catch (error) {
      console.error('Error finding media URL by source ID:', error);
      return null;
    }
  };

  const getYouTubeEmbedUrl = (url: string, startTimeFromData?: string): string => {
    if (!url || typeof url !== 'string') return '';

    try {
      const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (videoIdMatch && videoIdMatch[1]) {
        const videoId = videoIdMatch[1];
        let embedUrl = `https://www.youtube.com/embed/${videoId}`;

        // Extract timestamp from URL (&t= parameter) or use provided startTime
        let startTime = startTimeFromData;
        if (!startTime) {
          const timeMatch = url.match(/[&?]t=(\d+)/);
          if (timeMatch && timeMatch[1]) {
            startTime = timeMatch[1];
          }
        }

        // Add start time parameter if available
        if (startTime) {
          embedUrl += `?start=${startTime}`;
        }

        return embedUrl;
      }
      return url;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return url;
    }
  };

  const handleVideoFullscreen = (videoUrl: string) => {
    const videoHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Video Playback</title>
        <style>
          body { 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            height: 100vh; 
            margin: 0; 
            background: #000; 
          }
        </style>
      </head>
      <body>
        <video controls width="100%" style="max-width:100%; max-height:100%;">
          <source src="${videoUrl}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </body>
      </html>
    `;

    const videoWindow = window.open("", "_blank");
    if (videoWindow) {
      videoWindow.document.write(videoHTML);
      videoWindow.document.close();
    } else {
      alert("Please allow popups for this website");
    }
  };

  const handleAudioFullscreen = (audioUrl: string) => {
    const audioHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Audio Playback</title>
        <style>
          body { 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            height: 100vh; 
            margin: 0; 
            background: #000; 
          }
          audio {
            width: 100%; 
            max-width: 600px;
          }
        </style>
      </head>
      <body>
        <audio controls>
          <source src="${audioUrl}" type="audio/mpeg">
          Your browser does not support the audio tag.
        </audio>
      </body>
      </html>
    `;

    const audioWindow = window.open("", "_blank");
    if (audioWindow) {
      audioWindow.document.write(audioHTML);
      audioWindow.document.close();
    } else {
      alert("Please allow popups for this website");
    }
  };

  const handleYoutubeClick = (youtubeUrl: string) => {
    window.open(youtubeUrl, '_blank');
  };

  const initializeViewer = useCallback(() => {
    if (!imgRef.current || imageUrlList.length === 0) {
      console.error('Cannot initialize ViewerJS: missing ref or images');
      return false;
    }

    try {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }

      const images = imgRef.current.querySelectorAll('img');

      if (images.length === 0) {
        console.error('Manual init - No images found in container');
        return false;
      }

      viewerRef.current = new Viewer(imgRef.current, {
        title: false,
        focus: false,
        inline: false,
        button: true,
        navbar: true,
        tooltip: true,
        movable: true,
        zoomable: true,
        rotatable: true,
        scalable: true,
        transition: true,
        fullscreen: true,
        keyboard: true,
        toolbar: {
          zoomIn: { show: 1, size: 'large' },
          zoomOut: { show: 1, size: 'large' },
          oneToOne: { show: 1, size: 'large' },
          reset: { show: 1, size: 'large' },
          prev: { show: 1, size: 'large' },
          play: { show: 4, size: 'large' },
          next: { show: 1, size: 'large' },
          rotateLeft: { show: 1, size: 'large' },
          rotateRight: { show: 1, size: 'large' },
          flipHorizontal: { show: 1, size: 'large' },
          flipVertical: { show: 1, size: 'large' },
        },
      });
      return true;
    } catch (error) {
      console.error('Error in manual ViewerJS initialization:', error);
      return false;
    }
  }, [imageUrlList]);

  const handleImageClick = (index: number) => {

    // If ViewerJS is not initialized, try to initialize it
    if (!viewerRef.current) {
      const initialized = initializeViewer();
      if (!initialized) {
        console.error('Failed to initialize ViewerJS manually');
        return;
      }
      // Give it a moment to initialize
      setTimeout(() => {
        if (viewerRef.current) {
          try {
            viewerRef.current.view(index);
          } catch (error) {
            console.error('Error opening ViewerJS after manual init:', error);
          }
        }
      }, 100);
    } else {
      // ViewerJS is already initialized
      try {
        viewerRef.current.view(index);
      } catch (error) {
        console.error('Error opening ViewerJS:', error);
      }
    }
  };

  const navigateCarousel = useCallback((mediaType: string, direction: 'prev' | 'next', totalItems: number) => {
    if (!mediaType || totalItems <= 0) return;

    try {
      setCarouselIndices(prev => {
        const currentIndex = prev[mediaType] || 0;
        let newIndex;

        if (direction === 'next') {
          newIndex = (currentIndex + 1) % totalItems;
        } else {
          newIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
        }

        // Update the DOM to show/hide carousel items
        const carousel = document.getElementById(`carousel-${mediaType}`);
        if (carousel) {
          const items = carousel.querySelectorAll('.carousel-item');
          items.forEach((item, index) => {
            if (item && item.classList) {
              if (index === newIndex) {
                item.classList.remove('hidden');
                item.classList.add('block');
              } else {
                item.classList.remove('block');
                item.classList.add('hidden');
              }
            }
          });

          // Update dots
          const dots = carousel.parentElement?.querySelectorAll('button[aria-label*="Go to"]');
          dots?.forEach((dot, index) => {
            if (dot && dot.classList) {
              if (index === newIndex) {
                dot.classList.remove('bg-default-300');
                dot.classList.add('bg-primary');
              } else {
                dot.classList.remove('bg-primary');
                dot.classList.add('bg-default-300');
              }
            }
          });
        }

        return {
          ...prev,
          [mediaType]: newIndex
        };
      });
    } catch (error) {
      console.error('Error navigating carousel:', error);
    }
  }, []);

  const goToCarouselSlide = useCallback((mediaType: string, targetIndex: number, totalItems: number) => {
    if (!mediaType || targetIndex < 0 || targetIndex >= totalItems) return;

    try {
      setCarouselIndices(prev => {
        // Update the DOM to show/hide carousel items
        const carousel = document.getElementById(`carousel-${mediaType}`);
        if (carousel) {
          const items = carousel.querySelectorAll('.carousel-item');
          items.forEach((item, index) => {
            if (item && item.classList) {
              if (index === targetIndex) {
                item.classList.remove('hidden');
                item.classList.add('block');
              } else {
                item.classList.remove('block');
                item.classList.add('hidden');
              }
            }
          });

          // Update dots
          const dots = carousel.parentElement?.querySelectorAll('button[aria-label*="Go to"]');
          dots?.forEach((dot, index) => {
            if (dot && dot.classList) {
              if (index === targetIndex) {
                dot.classList.remove('bg-default-300');
                dot.classList.add('bg-primary');
              } else {
                dot.classList.remove('bg-primary');
                dot.classList.add('bg-default-300');
              }
            }
          });
        }

        return {
          ...prev,
          [mediaType]: targetIndex
        };
      });
    } catch (error) {
      console.error('Error going to carousel slide:', error);
    }
  }, []);

  const renderMediaItem = useCallback((source: MediaSource, index: number) => {
    if (!source || typeof index !== 'number') return null;

    const fileName = source?.file_name || `${source.mediaType || 'media'} ${index + 1}`;

    try {
      if (source.mediaType === 'youtube' && isVideoActive) {
        const youtubeUrl = source?.element_refs?.[0]?.url || '';
        if (!youtubeUrl) return null;

        // Extract start_time from element_refs as fallback
        const startTimeFromData = (source?.element_refs?.[0] as any)?.start_time;
        const embedUrl = getYouTubeEmbedUrl(youtubeUrl, startTimeFromData);
        if (!embedUrl) return null;

        return (
          <div key={`youtube-${index}`} className="relative p-2 bg-content2 rounded-xl">
            <iframe
              src={embedUrl}
              className="w-[40rem] h-[20rem] rounded-lg"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={fileName}
            />
            <Button
              isIconOnly
              size="sm"
              onPress={() => handleYoutubeClick(youtubeUrl)}
              className="absolute bottom-3 right-3 z-10 rounded-full bg-default-600 shadow-lg"
            >
              <Icon
                icon="material-symbols:open-in-new"
                width="1.2rem"
                height="1.2rem"
                style={{ color: '#fff' }}
              />
            </Button>
          </div>
        );
      } else if (source.mediaType === 'video' && isVideoActive) {
        const mediaUrl = getMediaUrlBySourceId(source.source_id);

        if (!mediaUrl || !mediaUrl.full_source_presigned_url) {
          return null;
        }

        return (
          <div key={`video-${index}`} className="relative p-2 bg-primary/10 rounded-xl">
            <video
              className="w-[40rem] h-[20rem] rounded-lg"
              controls
              preload="metadata"
            >
              <source src={mediaUrl.full_source_presigned_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <Button
              isIconOnly
              size="sm"
              onPress={() => handleVideoFullscreen(mediaUrl.full_source_presigned_url)}
              className="absolute bottom-3 right-3 z-10 rounded-full bg-primary shadow-lg"
            >
              <Icon
                icon="material-symbols:open-in-new"
                width="1.2rem"
                height="1.2rem"
                style={{ color: '#fff' }}
              />
            </Button>
          </div>
        );
      } else if (source.mediaType === 'audio' && isAudioActive) {
        const mediaUrl = getMediaUrlBySourceId(source.source_id);

        if (!mediaUrl || !mediaUrl.full_source_presigned_url) {
          return null;
        }

        return (
          <div key={`audio-${index}`} className="relative p-2 bg-[#000] rounded-xl">
            <div className=" flex flex-col items-center justify-center bg-[#000] rounded-lg border">
              <div className="flex flex-col items-center justify-center w-[40rem] h-[20rem] px-8">
                <Icon
                  icon="material-symbols:audio-file"
                  width="4rem"
                  height="4rem"
                  className="text-default-600 mb-6"
                />
                <audio
                  controls
                  className="w-[40rem] h-12 rounded-lg"
                  preload="metadata"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    outline: 'none'
                  }}
                >
                  <source src={mediaUrl.full_source_presigned_url} type="audio/mpeg" />
                  Your browser does not support the audio tag.
                </audio>
              </div>
            </div>
            <Button
              isIconOnly
              size="sm"
              onPress={() => handleAudioFullscreen(mediaUrl.full_source_presigned_url)}
              className="absolute bottom-3 right-3 z-10 rounded-full bg-default-600 shadow-lg"
            >
              <Icon
                icon="material-symbols:open-in-new"
                width="1.2rem"
                height="1.2rem"
                style={{ color: '#fff' }}
              />
            </Button>
          </div>
        );
      } else if (source.mediaType === 'image' && isImageActive) {
        if (!imageUrlList || imageUrlList.length === 0) return null;

        const imageUrl = imageUrlList.find(img => img?.s3_path === source.img_path);

        if (!imageUrl || !imageUrl.presigned_url) {
          return null;
        }

        return (
          <SmartImageContainer
            key={`image-${index}`}
            imageUrl={imageUrl.presigned_url}
            index={index}
            onImageClick={handleImageClick}
            imageDescription={source?.image_description}
          />
        );
      }

      return null;
    } catch (error) {
      console.error('Error rendering media item:', error, source);
      return null;
    }
  }, [getMediaUrlBySourceId, getYouTubeEmbedUrl, handleYoutubeClick, handleAudioFullscreen, handleVideoFullscreen, imageUrlList, handleImageClick]);

  const renderMediaGrid = useCallback((sources: MediaSource[]) => {
    if (!sources || sources.length === 0) return null;

    if (sources.length === 1) {
      return (
        <div className="flex justify-center">
          {renderMediaItem(sources[0], 0)}
        </div>
      );
    }

    // For multiple items, use carousel instead of accordion
    return (
      <div className="w-full">
        <div className="relative">
          {/* Carousel container */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Current item display */}
              <div id={`carousel-${sources[0].mediaType}`} className="relative">
                {sources.map((source, index) => (
                  <div
                    key={`carousel-item-${index}`}
                    className={`carousel-item ${index === (carouselIndices[sources[0].mediaType] || 0) ? 'block' : 'hidden'}`}
                    data-index={index}
                  >
                    {renderMediaItem(source, index)}
                  </div>
                ))}
              </div>

              {/* Navigation buttons */}
              {sources.length > 1 && (
                <>
                  <Button
                    isIconOnly
                    size="sm"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-70 text-white rounded-full"
                    onPress={() => navigateCarousel(sources[0].mediaType, 'prev', sources.length)}
                  >
                    <Icon icon="material-symbols:chevron-left" width="1.5rem" height="1.5rem" />
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-70 text-white rounded-full"
                    onPress={() => navigateCarousel(sources[0].mediaType, 'next', sources.length)}
                  >
                    <Icon icon="material-symbols:chevron-right" width="1.5rem" height="1.5rem" />
                  </Button>
                </>
              )}

              {/* Dots indicator */}
              {sources.length > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  {sources.map((_, index) => (
                    <button
                      key={`dot-${index}`}
                      className={`w-3 h-3 rounded-full transition-colors ${index === (carouselIndices[sources[0].mediaType] ?? 0) ? 'bg-primary' : 'bg-default-300'
                        }`}
                      onClick={() => goToCarouselSlide(sources[0].mediaType, index, sources.length)}
                      aria-label={`Go to ${sources[0].mediaType} ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [renderMediaItem, carouselIndices, navigateCarousel, goToCarouselSlide]);

  const renderImageTab = useCallback(() => {
    if (!imageUrlList || imageUrlList.length === 0) {
      return <div className="text-center text-default-500 py-8">No images available</div>;
    }

    try {
      // Convert imageUrlList to MediaSource format for carousel
      const imageSources: MediaSource[] = imageUrlList.map((image, index) => {
        if (!image || !image.presigned_url) {
          return null;
        }
        return {
          source_id: `image-${index}`,
          file_name: `Image ${index + 1}`,
          page_labels: [],
          element_type: 'image',
          ranking: index,
          mediaType: 'image' as const,
          img_path: image.s3_path || '',
          image_description: categorizedSources.image[index]?.image_description
        };
      }).filter(Boolean) as MediaSource[];

      if (imageSources.length === 0) {
        return <div className="text-center text-default-500 py-8">No valid images available</div>;
      }

       return (
         <div className="relative">
           {/* ViewerJS container - always present with all images */}
           <div ref={imgRef} style={{ display: 'none' }}>
             {imageUrlList.map((image, index) => {
               if (!image || !image.presigned_url) return null;
               return (
                 <img
                   key={`viewer-img-${index}`}
                   src={image.presigned_url}
                   alt={`Image ${index + 1}`}
                   data-original={image.presigned_url}
                   onError={(e) => {
                     // Hide the image when it fails to load
                     (e.currentTarget as HTMLElement).style.display = 'none';
                   }}
                 />
               );
             })}
           </div>

           {/* Use the same carousel system as other media types */}
           {renderMediaGrid(imageSources)}
         </div>
       );
    } catch (error) {
      console.error('Error rendering image tab:', error);
      return <div className="text-center text-danger py-8">Error loading images</div>;
    }
  }, [imageUrlList, renderMediaGrid, categorizedSources, carouselIndices]);

  const renderLoadingState = useCallback(() => (
    <div className="flex flex-wrap">
      <div className="h-[13rem] w-full flex justify-center m-1 items-center rounded-md bg-[#fff]">
        <CircularProgress
          aria-label="Loading..."
          size="lg"
          value={value}
          classNames={{
            svg: 'w-12 h-12 drop-shadow-md',
            indicator: 'stroke-black',
            track: 'stroke-black/10',
          }}
          strokeWidth={2}
          color="default"
          valueLabel={
            <Icon
              icon="material-symbols:media-library"
              width="1.2rem"
              height="1.2rem"
              className="text-[#000]"
            />
          }
          showValueLabel={true}
        />
      </div>
    </div>
  ), [value]);

  const renderErrorState = useCallback(() => (
    <div className="text-sm py-3 text-danger h-[15rem] border-1 flex items-center justify-center">
      {(generateMediaError as any)?.message || (generateImageError as any)?.message || 'Error Fetching Media'}
    </div>
  ), [generateMediaError, generateImageError]);

  // Memoize tab selection handler
  const handleTabChange = useCallback((key: any) => {
    setSelectedTab(key as string);
  }, []);
  // Check if any media exists and is active
  const hasAnyMedia = useMemo(() =>
    (imageUrlList.length > 0 && isImageActive && isImagePresentInResponse) ||
    (categorizedSources.video.length > 0 && isVideoActive) ||
    (categorizedSources.audio.length > 0 && isAudioActive) ||
    (categorizedSources.youtube.length > 0 && isVideoActive)
    , [categorizedSources, imageUrlList, isImageActive, isImagePresentInResponse, isVideoActive, isAudioActive]);

  if (!hasAnyMedia) {
    return null;
  }

  const fileBasedSources = [...categorizedSources.video, ...categorizedSources.audio];
  const hasImageSources = isImagePresentInResponse && imageUrlList.length > 0;

  if ((generateMediaLoading && fileBasedSources.length > 0) || (generateImageLoading && hasImageSources)) {
    return <div className="my-2 rounded-md">{renderLoadingState()}</div>;
  }

  if (generateMediaError || generateImageError) {
    return <div className="my-2 rounded-md">{renderErrorState()}</div>;
  }

  return (
    <div className="m-2 rounded-md">
      {hasAnyMedia && (
        <Card>
          <CardBody>
            <div className="max-h-[40rem] overflow-y-auto">
              <Tabs
                aria-label="Media options"
                selectedKey={selectedTab}
                onSelectionChange={handleTabChange}
                variant="bordered"
                color="primary"
              >
                {isImagePresentInResponse && imageUrlList.length > 0 && isImageActive && (
                  <Tab
                    key="image"
                    title={
                      <div className="flex items-center space-x-2">
                        <Icon icon="material-symbols:image" />
                        <span>Images ({imageUrlList.length})</span>
                      </div>
                    }
                  >
                    {renderImageTab()}
                  </Tab>
                )}

                {categorizedSources.video.length > 0 && isVideoActive && (
                  <Tab
                    key="video"
                    title={
                      <div className="flex items-center space-x-2">
                        <Icon icon="material-symbols:video-file" />
                        <span>Videos ({categorizedSources.video.length})</span>
                      </div>
                    }
                  >
                    {renderMediaGrid(categorizedSources.video)}
                  </Tab>
                )}

                {categorizedSources.audio.length > 0 && isAudioActive && (
                  <Tab
                    key="audio"
                    title={
                      <div className="flex items-center space-x-2">
                        <Icon icon="material-symbols:audio-file" />
                        <span>Audio ({categorizedSources.audio.length})</span>
                      </div>
                    }
                  >
                    {renderMediaGrid(categorizedSources.audio)}
                  </Tab>
                )}

                {categorizedSources.youtube.length > 0 && isVideoActive && (
                  <Tab
                    key="youtube"
                    title={
                      <div className="flex items-center space-x-2">
                        <Icon icon="logos:youtube-icon" />
                        <span>YouTube ({categorizedSources.youtube.length})</span>
                      </div>
                    }
                  >
                    {renderMediaGrid(categorizedSources.youtube)}
                  </Tab>
                )}
              </Tabs>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

// Smart image container that adjusts size based on aspect ratio
interface SmartImageContainerProps {
  imageUrl: string;
  index: number;
  onImageClick: (index: number) => void;
  imageDescription?: string;
}
const SmartImageContainer = ({
  imageUrl,
  index,
  onImageClick,
  imageDescription
}: SmartImageContainerProps) => {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      setAspectRatio(ratio);
      setIsLoaded(true);
    };
    img.onerror = () => {
      setIsLoaded(true); // Still show container even if image fails
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Determine container class based on aspect ratio
  const getContainerClass = () => {
    if (!isLoaded || aspectRatio === null) {
      // Default size while loading
      return "w-[24rem] h-[20rem]";
    }

    // Square-ish images (aspect ratio between 0.8 and 1.25) - like QR codes, logos, portraits
    if (aspectRatio >= 0.8 && aspectRatio <= 1.25) {
      return "w-[24rem] h-[24rem]";
    }

    // Wide images (aspect ratio > 1.25) - leverage full width
    if (aspectRatio > 1.25) {
      return "w-full max-w-4xl h-[20rem]";
    }

    // Tall images (aspect ratio < 0.8) - moderate width, more height
    return "w-[20rem] h-[28rem]";
  };

  return (
    <div className="relative p-2 bg-default-100 rounded-xl">
      {/* Image description tooltip - top-right of image card so tooltip appears near image */}
      {imageDescription && (
      <Tooltip
      content={
        <div className="max-w-80 max-h-64 overflow-y-auto p-2 text-sm">
          {imageDescription}
        </div>
      }
      placement="right"
      color="warning"
      showArrow
    >
      <Button
        isIconOnly
        size="sm"
        className="absolute top-3 right-3 z-20 rounded-full bg-black bg-opacity-70 shadow-lg min-w-8 w-8 h-8"
      >
        <Icon icon="ph:info-light" width="1.2rem" height="1.2rem" style={{ color: '#fff' }} />
      </Button>
    </Tooltip>
      )}
      <div className={`${getContainerClass()} flex items-center justify-center rounded-lg overflow-hidden mx-auto transition-all duration-300`}>
        <img
          src={imageUrl}
          alt={`Image ${index + 1}`}
          className="max-w-full max-h-full w-auto h-auto rounded-lg cursor-pointer object-contain"
          style={{ objectFit: 'contain' }}
          onClick={() => onImageClick(index)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onImageClick(index);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`Open image ${index + 1} in viewer`}
          onError={(e) => {
            // Hide the image container when image fails to load
            const container = e.currentTarget.closest('.relative') as HTMLElement;
            if (container) {
              container.style.display = 'none';
            } else {
              // Fallback: hide the image itself if container not found
              (e.currentTarget as HTMLElement).style.display = 'none';
            }
          }}
        />
      </div>
      <div className="absolute bottom-3 left-3 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
        Image {index + 1}
      </div>
      <Button
        isIconOnly
        size="sm"
        onPress={() => onImageClick(index)}
        className="absolute bottom-3 right-3 z-10 rounded-full bg-primary shadow-lg"
      >
        <Icon
          icon="ion:expand-outline"
          width="1.2rem"
          height="1.2rem"
          style={{ color: '#fff' }}
        />
      </Button>
    </div>
  );
};

export default UnifiedMediaPreview; 
